import { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SEO from "../components/SEO";

const CONSENT_KEY = "cardoso_cookie_consent";

const sections = [
  {
    title: "1. Wie zijn wij?",
    content: [
      "Cardoso Bouw en Onderhoud is verantwoordelijk voor de verwerking van persoonsgegevens zoals beschreven in dit privacybeleid.",
      "Bedrijfsgegevens: Cardoso Bouw en Onderhoud, Merwedeweg 5C1, 3621 LP Breukelen. KVK: 85310646. BTW: NL004077138B58. E-mail: info@cardosobouw.nl. Telefoon: 06 85 07 10 09.",
    ],
  },
  {
    title: "2. Welke gegevens verwerken wij?",
    content: [
      "Wanneer u een offerte aanvraagt of contact met ons opneemt via het formulier, telefoon, e-mail of WhatsApp, verwerken wij de gegevens die u zelf aan ons verstrekt: uw naam, e-mailadres, telefoonnummer, adres van het project, de gewenste dienst en datum, uw bericht en eventueel meegestuurde foto's van de situatie.",
      "Bij een bezoek aan onze website verwerken wij, uitsluitend na uw toestemming, analytische gegevens via Google Analytics (zoals bezochte pagina's, apparaattype en globale locatie). Deze gegevens zijn zoveel mogelijk geanonimiseerd.",
    ],
  },
  {
    title: "3. Waarvoor gebruiken wij uw gegevens?",
    content: [
      "Wij gebruiken uw gegevens om uw offerte aanvraag te behandelen, contact met u op te nemen, een passende offerte op te stellen en – bij opdracht – de werkzaamheden uit te voeren en te factureren.",
      "Analytische gegevens gebruiken wij om onze website te verbeteren en te begrijpen hoe bezoekers onze site gebruiken.",
    ],
  },
  {
    title: "4. Op welke grondslag?",
    content: [
      "Wij verwerken uw gegevens op basis van de uitvoering van een (aanstaande) overeenkomst, ons gerechtvaardigd belang om uw vraag te beantwoorden en, voor analytische cookies, uw toestemming. Voor onze administratie geldt een wettelijke verplichting.",
    ],
  },
  {
    title: "5. Hoe lang bewaren wij uw gegevens?",
    content: [
      "Offerte aanvragen en meegestuurde foto's bewaren wij maximaal 2 jaar na het laatste contact, tenzij er een opdracht uit voortkomt. Gegevens die nodig zijn voor onze financiële administratie bewaren wij 7 jaar conform de wettelijke bewaartermijn.",
    ],
  },
  {
    title: "6. Met wie delen wij uw gegevens?",
    content: [
      "Wij verkopen uw gegevens nooit aan derden. Wij delen gegevens alleen met partijen die nodig zijn voor onze dienstverlening: onze e-mailverzenddienst (voor het versturen van bevestigingen), onze hostingpartij (voor het veilig opslaan van aanvragen) en Google (Analytics, uitsluitend na toestemming). Met deze partijen zijn passende afspraken gemaakt over de bescherming van uw gegevens.",
    ],
  },
  {
    title: "7. Cookies",
    content: [
      "Onze website gebruikt noodzakelijke cookies voor het functioneren van de site en, alleen na uw toestemming, analytische cookies van Google Analytics. U kunt uw keuze op elk moment wijzigen via de knop onderaan deze pagina.",
    ],
  },
  {
    title: "8. Uw rechten",
    content: [
      "U heeft het recht om uw persoonsgegevens in te zien, te laten corrigeren of te laten verwijderen. Ook kunt u bezwaar maken tegen de verwerking, uw toestemming intrekken en verzoeken om overdracht van uw gegevens. Stuur uw verzoek naar info@cardosobouw.nl; wij reageren binnen 4 weken.",
      "Bent u niet tevreden over de manier waarop wij met uw gegevens omgaan? Dan kunt u een klacht indienen bij de Autoriteit Persoonsgegevens.",
    ],
  },
  {
    title: "9. Beveiliging",
    content: [
      "Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen verlies of onrechtmatige verwerking, zoals een beveiligde verbinding (HTTPS) en toegangsbeveiliging op onze systemen.",
    ],
  },
  {
    title: "10. Wijzigingen",
    content: [
      "Wij kunnen dit privacybeleid aanpassen. De meest actuele versie vindt u altijd op deze pagina. Laatst bijgewerkt: juni 2026.",
    ],
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resetCookies = () => {
    localStorage.removeItem(CONSENT_KEY);
    toast.success("Cookievoorkeuren gereset. De cookiemelding verschijnt opnieuw.");
    setTimeout(() => window.location.reload(), 800);
  };

  return (
    <div data-testid="privacy-page">
      <SEO
        title="Privacybeleid - Cardoso Bouw en Onderhoud"
        description="Lees hoe Cardoso Bouw en Onderhoud omgaat met uw persoonsgegevens, foto's en cookies. Transparant en volgens de AVG."
        breadcrumb="Privacybeleid"
      />
      <section className="relative h-[40vh] min-h-[320px] flex items-end bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-16 w-full">
          <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Privacy</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase leading-tight">
            Privacybeleid
          </h1>
          <p className="text-white/70 text-base mt-4 max-w-lg">
            Wij gaan zorgvuldig om met uw gegevens. Hieronder leest u welke gegevens wij verwerken en waarom.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl space-y-10">
            {sections.map((section) => (
              <div key={section.title}>
                <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-4">
                  {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((p, i) => (
                    <p key={i} className="text-slate-600 text-base leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            ))}

            <div className="border-2 border-slate-200 p-8">
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A] mb-3">
                Cookievoorkeuren wijzigen
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Wilt u uw cookiekeuze aanpassen? Klik hieronder om de cookiemelding opnieuw te tonen.
              </p>
              <button
                onClick={resetCookies}
                data-testid="privacy-reset-cookies-btn"
                className="bg-[#005F5F] hover:bg-[#C5A059] text-white font-bold uppercase tracking-wider px-6 py-3 text-xs transition-colors"
              >
                Cookievoorkeuren wijzigen
              </button>
            </div>

            <p className="text-slate-500 text-sm">
              Vragen over dit privacybeleid? <Link to="/contact" className="text-[#005F5F] font-medium hover:text-[#C5A059] transition-colors">Neem contact met ons op</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
